import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import PharmatechLayout from "../components/PharmatechLayout";
import axios from "axios";
import { toast } from "sonner";
import {
  ScanLine, Upload, AlertTriangle, CheckCircle2, XCircle,
  FileText, Pill, Building2, Loader2, ChevronDown, ChevronRight,
  History, Stethoscope, Shield, Info, Baby, Heart, User,
  MessageCircle, BookOpen, Activity, Scale, AlertOctagon,
  Zap, Search, X, Plus
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

const SEVERITY_STYLES = {
  critique: { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", badge: "bg-red-600 text-white", icon: XCircle },
  grave: { bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-800", badge: "bg-orange-500 text-white", icon: AlertTriangle },
  moderee: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", badge: "bg-amber-500 text-white", icon: Info },
  faible: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-500 text-white", icon: Info },
  info: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", badge: "bg-slate-500 text-white", icon: Info },
};

const STATUS_STYLES = {
  conforme: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200", icon: CheckCircle2, label: "Conforme" },
  a_risque: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle, label: "A risque" },
  rejet_probable: { bg: "bg-red-500", text: "text-red-700", light: "bg-red-50", border: "border-red-200", icon: XCircle, label: "Rejet probable" },
};

const CLINICAL_OPTIONS = [
  { key: "grossesse", label: "Grossesse", icon: Heart, color: "text-pink-600", bgActive: "bg-pink-100 border-pink-300" },
  { key: "allaitement", label: "Allaitement", icon: Baby, color: "text-purple-600", bgActive: "bg-purple-100 border-purple-300" },
  { key: "insuffisance_renale", label: "Insuff. renale", icon: Activity, color: "text-red-600", bgActive: "bg-red-100 border-red-300" },
  { key: "insuffisance_hepatique", label: "Insuff. hepatique", icon: AlertOctagon, color: "text-amber-700", bgActive: "bg-amber-100 border-amber-300" },
];

/* ===== Patient Profile ===== */
function PatientProfileCard({ age, setAge, ageUnit, setAgeUnit, poids, setPoids, clinicalStatus, toggleClinical, expanded, setExpanded, patientName, setPatientName, patientPhone, setPatientPhone }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="patient-profile-card">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition" data-testid="toggle-patient-profile">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-bold text-slate-700">Profil Patient</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 font-semibold">Optionnel</span>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Renseignez le profil pour obtenir un calcul de posologie adapte et des alertes cliniques personnalisees.
          </p>
          {/* Identité patient (optionnel - pour historique fiche conseil) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Nom du patient <span className="text-slate-300 normal-case">· optionnel</span></label>
              <input type="text" value={patientName} onChange={e => setPatientName(e.target.value)}
                placeholder="ex: Aminata KOUAME" data-testid="patient-name-input"
                className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Telephone <span className="text-slate-300 normal-case">· retour patient</span></label>
              <input type="tel" value={patientPhone} onChange={e => setPatientPhone(e.target.value)}
                placeholder="+225 07 11 22 33" data-testid="patient-phone-input"
                className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Age</label>
              <div className="flex gap-1.5">
                <input type="number" min="0" max="150" value={age} onChange={e => setAge(e.target.value)}
                  placeholder="--" data-testid="patient-age-input"
                  className="flex-1 w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-teal-200 focus:border-teal-400 outline-none" />
                <select value={ageUnit} onChange={e => setAgeUnit(e.target.value)} data-testid="patient-age-unit"
                  className="px-2 py-2 rounded-lg text-xs border border-slate-200 bg-white text-slate-600 focus:ring-2 focus:ring-teal-200">
                  <option value="ans">ans</option>
                  <option value="mois">mois</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Poids (kg)</label>
              <div className="relative">
                <input type="number" min="0" max="300" step="0.1" value={poids} onChange={e => setPoids(e.target.value)}
                  placeholder="--" data-testid="patient-weight-input"
                  className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-teal-200 focus:border-teal-400 outline-none pr-10" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">kg</span>
              </div>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-2">Contexte clinique</label>
            <div className="grid grid-cols-2 gap-2">
              {CLINICAL_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const active = clinicalStatus.includes(opt.key);
                return (
                  <button key={opt.key} onClick={() => toggleClinical(opt.key)} data-testid={`clinical-${opt.key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold border transition-all ${active ? `${opt.bgActive} ${opt.color}` : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Quick Check: Autocomplete + Chips ===== */
function QuickCheckInput({ selectedMeds, setSelectedMeds, onCheck, checking }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const searchMeds = async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/prescription-scanner/medications/search?q=${encodeURIComponent(q)}`);
      setSuggestions(res.data.results || []);
      setShowSuggestions(true);
    } catch { setSuggestions([]); }
  };

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchMeds(val), 250);
  };

  const addMed = (med) => {
    const label = med.noms?.[0] || med.dci;
    if (!selectedMeds.find(m => m.dci === med.dci)) {
      setSelectedMeds(prev => [...prev, { dci: med.dci, label, classe: med.classe }]);
    }
    setQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeMed = (dci) => {
    setSelectedMeds(prev => prev.filter(m => m.dci !== dci));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3" data-testid="quick-check-input">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-4 h-4 text-amber-500" />
        <h4 className="text-sm font-bold text-slate-700">Saisir les medicaments</h4>
      </div>

      {/* Chips */}
      {selectedMeds.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="selected-meds-chips">
          {selectedMeds.map((m) => (
            <span key={m.dci} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-100 text-sky-800 text-xs font-semibold border border-sky-200">
              <Pill className="w-3 h-3" />
              {m.label}
              <button onClick={() => removeMed(m.dci)} className="ml-0.5 hover:text-red-600 transition" data-testid={`remove-med-${m.dci}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input ref={inputRef} type="text" value={query} onChange={e => handleInput(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Tapez un nom de medicament (ex: Doliprane, Amoxicilline...)"
          data-testid="quick-check-search"
          className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none" />

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="suggestions-dropdown">
            {suggestions.map((s, i) => (
              <button key={i} onMouseDown={() => addMed(s)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-sky-50 transition text-left border-b border-slate-50 last:border-0">
                <div>
                  <span className="text-sm font-medium text-slate-800">{s.noms?.[0] || s.dci}</span>
                  <span className="text-xs text-slate-400 ml-2">({s.dci})</span>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">{s.classe}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Check button */}
      <button onClick={onCheck} disabled={selectedMeds.length < 1 || checking}
        data-testid="quick-check-button"
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold text-white transition active:scale-[0.98] ${selectedMeds.length < 1 || checking ? "bg-slate-300 cursor-not-allowed" : "bg-amber-500 hover:bg-amber-600"}`}>
        {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        {checking ? "Analyse en cours..." : `Verifier ${selectedMeds.length} medicament(s)`}
      </button>
    </div>
  );
}

/* ===== Alert Section ===== */
function AlertSection({ cat, alerts }) {
  if (!alerts || alerts.length === 0) return null;
  const catConfig = {
    administratif: { icon: FileText, label: "Alertes Administratives", color: "text-orange-600", accent: "border-l-orange-400" },
    clinique: { icon: Stethoscope, label: "Alertes Cliniques", color: "text-red-600", accent: "border-l-red-400" },
    assurance: { icon: Building2, label: "Alertes Assurance", color: "text-violet-600", accent: "border-l-violet-400" },
  };
  const cfg = catConfig[cat];
  const CatIcon = cfg.icon;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`alerts-${cat}`}>
      <div className="p-4 border-b border-slate-100">
        <h4 className={`text-sm font-bold flex items-center gap-2 ${cfg.color}`}>
          <CatIcon className="w-4 h-4" /> {cfg.label} ({alerts.length})
        </h4>
      </div>
      <div className="p-3 space-y-2">
        {alerts.map((a, i) => {
          const sev = SEVERITY_STYLES[a.severite] || SEVERITY_STYLES.info;
          const SevIcon = sev.icon;
          return (
            <div key={i} className={`rounded-lg border-l-4 ${cfg.accent} ${sev.bg} border ${sev.border} p-3`}>
              <div className="flex items-start gap-2">
                <SevIcon className={`w-4 h-4 mt-0.5 ${sev.text} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${sev.badge} flex-shrink-0 uppercase`}>{a.severite}</span>
                  </div>
                  <p className={`text-xs font-medium ${sev.text} leading-relaxed`}>{a.message}</p>
                  {a.conduite && (
                    <p className="text-[11px] text-slate-600 mt-1.5 bg-white/60 rounded px-2 py-1 leading-relaxed">
                      <span className="font-bold">Conduite a tenir :</span> {a.conduite}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== Patient Counseling Section ===== */
function ConseilsPatientSection({ conseils }) {
  const [open, setOpen] = useState(true);
  if (!conseils || conseils.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-200 shadow-sm overflow-hidden" data-testid="conseils-patient-section">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-teal-50/50 transition">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-teal-600" />
          <h4 className="text-sm font-bold text-teal-800">Ce que le pharmacien doit dire au patient</h4>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-600 text-white font-bold">{conseils.length}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-teal-500" /> : <ChevronRight className="w-4 h-4 text-teal-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {conseils.map((c, i) => (
            <div key={i} className="bg-white rounded-lg border border-teal-100 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Pill className="w-3.5 h-3.5 text-teal-600" />
                <span className="text-xs font-bold text-teal-800">{c.medicament}</span>
                {c.dci && c.dci !== c.medicament && <span className="text-[10px] text-slate-400">({c.dci})</span>}
              </div>
              <p className="text-xs text-slate-700 leading-relaxed pl-5">{c.conseil}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===== Medication Table ===== */
function MedicationTable({ medicaments, hasPatientProfile }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="medications-table">
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <Pill className="w-4 h-4 text-sky-500" />
        <h4 className="text-sm font-bold text-slate-700">{medicaments?.length || 0} Medicament(s)</h4>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden p-3 space-y-3">
        {(medicaments || []).map((m, i) => (
          <div key={i} className="border border-slate-100 rounded-lg p-3 space-y-2" data-testid={`med-card-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-800">{m.nom_original}</span>
              {m.connu_en_base ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Ref.</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">Inconnu</span>
              )}
            </div>
            {m.dci && <p className="text-[11px] text-slate-500"><span className="font-medium text-slate-400">DCI:</span> {m.dci}</p>}
            {m.classe && <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">{m.classe}</span>}
            {m.posologie_prescrite && <p className="text-[11px] text-slate-600"><span className="text-slate-400">Prescrit:</span> {m.posologie_prescrite}</p>}
            {m.montant_estime && <p className="text-xs text-slate-700 font-semibold">{fmt(m.montant_estime)} FCFA</p>}
            {hasPatientProfile && m.posologie_recommandee?.posologie_standard && (
              <div className="bg-teal-50 border border-teal-100 rounded-lg p-2 mt-1">
                <p className="text-[10px] font-bold text-teal-700 uppercase mb-0.5">
                  <Scale className="w-3 h-3 inline mr-1" />Posologie ({m.posologie_recommandee.type_patient})
                </p>
                <p className="text-[11px] text-teal-800">{m.posologie_recommandee.posologie_standard}</p>
                {m.posologie_recommandee.posologie_calculee && (
                  <p className="text-[11px] text-teal-900 font-semibold mt-0.5">Calcule : {m.posologie_recommandee.posologie_calculee}</p>
                )}
              </div>
            )}
            {m.restriction && <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{m.restriction}</span>}
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="text-left py-2 px-3">Medicament</th>
              <th className="text-left py-2 px-3">DCI / Classe</th>
              <th className="text-left py-2 px-3">Posologie prescrite</th>
              {hasPatientProfile && <th className="text-left py-2 px-3">Posologie recommandee</th>}
              <th className="text-right py-2 px-3">Prix est.</th>
              <th className="text-center py-2 px-3">Statut</th>
            </tr>
          </thead>
          <tbody>
            {(medicaments || []).map((m, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-3">
                  <span className="font-medium text-slate-800">{m.nom_original}</span>
                  {m.dosage && <span className="text-xs text-slate-400 ml-1">{m.dosage}</span>}
                </td>
                <td className="py-2 px-3">
                  <p className="text-xs text-slate-600">{m.dci || "--"}</p>
                  {m.classe && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">{m.classe}</span>}
                </td>
                <td className="py-2 px-3 text-xs text-slate-600">
                  {m.posologie_prescrite || "--"}
                  {m.duree && <span className="block text-slate-400">{m.duree}</span>}
                </td>
                {hasPatientProfile && (
                  <td className="py-2 px-3">
                    {m.posologie_recommandee?.posologie_standard ? (
                      <div className="text-xs">
                        <p className="text-teal-700">{m.posologie_recommandee.posologie_standard}</p>
                        {m.posologie_recommandee.posologie_calculee && (
                          <p className="text-teal-900 font-semibold">{m.posologie_recommandee.posologie_calculee}</p>
                        )}
                      </div>
                    ) : <span className="text-xs text-slate-400">--</span>}
                  </td>
                )}
                <td className="py-2 px-3 text-right text-slate-700 text-xs">{m.montant_estime ? `${fmt(m.montant_estime)} F` : "--"}</td>
                <td className="py-2 px-3 text-center">
                  {m.connu_en_base ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Ref.</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">Inconnu</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== MAIN PAGE ===== */
export default function PrescriptionScannerPage() {
  const { token } = useAuth();
  const [mode, setMode] = useState("classique");
  const [assurance, setAssurance] = useState("");
  const [insurances, setInsurances] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(false);

  // Patient profile
  const [age, setAge] = useState("");
  const [ageUnit, setAgeUnit] = useState("ans");
  const [poids, setPoids] = useState("");
  const [clinicalStatus, setClinicalStatus] = useState([]);
  const [patientName, setPatientName] = useState("");
  const [patientPhone, setPatientPhone] = useState("");

  // Quick check
  const [selectedMeds, setSelectedMeds] = useState([]);
  const [checking, setChecking] = useState(false);

  // Observance (calendrier livret) — par DCI : {dci: {duree, freq}}
  const [observanceConfig, setObservanceConfig] = useState({});
  const [showObservance, setShowObservance] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/prescription-scanner/insurances`).then(r => setInsurances(r.data.insurances)).catch(() => {});
    axios.get(`${API}/prescription-scanner/history?limit=10`, { headers }).then(r => setHistory(r.data.scans)).catch(() => {});
  }, [token]);

  const toggleClinical = (key) => {
    setClinicalStatus(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const getPatientProfile = () => {
    const ageMois = age ? (ageUnit === "ans" ? parseInt(age) * 12 : parseInt(age)) : null;
    return { ageMois, poidsKg: poids ? parseFloat(poids) : null, statuts: clinicalStatus };
  };

  const handleScan = async (file) => {
    if (!file) return;
    if (mode === "assurance" && !assurance) {
      toast.error("Selectionnez une assurance pour le mode assurance");
      return;
    }
    setScanning(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", mode);
      if (mode === "assurance" && assurance) formData.append("assurance", assurance);
      const pp = getPatientProfile();
      if (pp.ageMois !== null) formData.append("patient_age_mois", pp.ageMois);
      if (pp.poidsKg !== null) formData.append("patient_poids_kg", pp.poidsKg);
      if (pp.statuts.length > 0) formData.append("statut_clinique", pp.statuts.join(","));
      const res = await axios.post(`${API}/prescription-scanner/scan`, formData, { headers, timeout: 90000 });
      setResult(res.data);
      toast.success("Analyse clinique terminee !");
      axios.get(`${API}/prescription-scanner/history?limit=10`, { headers }).then(r => setHistory(r.data.scans)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'analyse");
    } finally {
      setScanning(false);
    }
  };

  const handleQuickCheck = async () => {
    if (selectedMeds.length < 1) return;
    setChecking(true);
    setResult(null);
    try {
      const pp = getPatientProfile();
      const body = {
        medicaments: selectedMeds.map(m => m.dci),
        patient_age_mois: pp.ageMois,
        patient_poids_kg: pp.poidsKg,
        statut_clinique: pp.statuts,
        patient_name: patientName.trim() || null,
        patient_phone: patientPhone.trim() || null,
      };
      const res = await axios.post(`${API}/prescription-scanner/quick-check`, body, { headers });
      setResult(res.data);
      toast.success("Verification terminee !");
      axios.get(`${API}/prescription-scanner/history?limit=10`, { headers }).then(r => setHistory(r.data.scans)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de verification");
    } finally {
      setChecking(false);
    }
  };

  // Conseil Express Comptoir : ouvre la fiche imprimable dans un nouvel onglet
  const handlePrintConseil = async () => {
    const checkId = result?.check_id;
    if (!checkId) return;
    try {
      const params = new URLSearchParams();
      const obj = {};
      Object.entries(observanceConfig).forEach(([dci, cfg]) => {
        if (cfg && (cfg.duree || cfg.freq)) {
          obj[dci] = [parseInt(cfg.duree) || 7, parseInt(cfg.freq) || 3];
        }
      });
      if (Object.keys(obj).length > 0) {
        params.set("observance", JSON.stringify(obj));
      }
      const qs = params.toString();
      const url = `${API}/prescription-scanner/conseil-express/${checkId}${qs ? "?" + qs : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        toast.error("Fiche introuvable");
        return;
      }
      const html = await res.text();
      const blob = new Blob([html], { type: "text/html" });
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Activez les popups pour imprimer la fiche conseil");
      } else {
        toast.success("Fiche conseil prête à imprimer");
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (e) {
      toast.error("Erreur lors de la generation de la fiche");
    }
  };

  const scoring = result?.scoring;
  const statusStyle = scoring ? STATUS_STYLES[scoring.statut] : null;
  const StatusIcon = statusStyle?.icon;
  const hasPatientProfile = result?.patient_profil && (result.patient_profil.age_mois || result.patient_profil.poids_kg || result.patient_profil.statuts?.length > 0);

  return (
    <PharmatechLayout title="Scanner d'Ordonnances" subtitle="Analyse clinique avancee - Posologie, interactions, conseils patient">
      <div className="space-y-4" data-testid="prescription-scanner-page">

        {/* Mode Selection: 3 modes */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col gap-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-2">Mode d'analyse</label>
              <div className="flex gap-2">
                <button data-testid="mode-classique" onClick={() => { setMode("classique"); setResult(null); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition border ${mode === "classique" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  <Stethoscope className="w-4 h-4" /> <span className="hidden sm:inline">Ordonnance</span> Classique
                </button>
                <button data-testid="mode-assurance" onClick={() => { setMode("assurance"); setResult(null); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition border ${mode === "assurance" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  <Shield className="w-4 h-4" /> Assurance
                </button>
                <button data-testid="mode-rapide" onClick={() => { setMode("rapide"); setResult(null); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition border ${mode === "rapide" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  <Zap className="w-4 h-4" /> <span className="hidden sm:inline">Verification</span> Rapide
                </button>
              </div>
            </div>

            {mode === "assurance" && (
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-2">Assurance / Mutuelle</label>
                <select value={assurance} onChange={e => setAssurance(e.target.value)} data-testid="assurance-select"
                  className="w-full px-3 py-2.5 rounded-lg text-sm border border-slate-200 bg-white text-slate-800 focus:ring-2 focus:ring-violet-300">
                  <option value="">-- Selectionnez --</option>
                  {insurances.map(ins => (
                    <option key={ins.code} value={ins.code}>{ins.nom} (plafond: {fmt(ins.plafond_ordonnance)} F)</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className={`mt-3 p-3 rounded-lg text-xs leading-relaxed ${mode === "classique" ? "bg-sky-50 text-sky-800 border border-sky-100" : mode === "assurance" ? "bg-violet-50 text-violet-800 border border-violet-100" : "bg-amber-50 text-amber-800 border border-amber-100"}`}>
            {mode === "classique" ? (
              <><BookOpen className="w-3.5 h-3.5 inline mr-1" /><strong>Mode Classique :</strong> Scannez une ordonnance. Interactions, posologie, contre-indications, doublons et conseils patient. Sources : VIDAL, Dorosz, ANSM, Lexicomp, Micromedex.</>
            ) : mode === "assurance" ? (
              <><Shield className="w-3.5 h-3.5 inline mr-1" /><strong>Mode Assurance :</strong> Analyse clinique + validation administrative (cachet, signature) + couverture assurance (remboursabilite, plafond, generiques).</>
            ) : (
              <><Zap className="w-3.5 h-3.5 inline mr-1" /><strong>Verification Rapide :</strong> Tapez directement 2 ou 3 noms de medicaments pour verifier les interactions au comptoir — sans scanner d'image. Resultat instantane.</>
            )}
          </div>
        </div>

        {/* Patient Profile */}
        <PatientProfileCard
          age={age} setAge={setAge} ageUnit={ageUnit} setAgeUnit={setAgeUnit}
          poids={poids} setPoids={setPoids}
          clinicalStatus={clinicalStatus} toggleClinical={toggleClinical}
          expanded={profileExpanded} setExpanded={setProfileExpanded}
          patientName={patientName} setPatientName={setPatientName}
          patientPhone={patientPhone} setPatientPhone={setPatientPhone}
        />

        {/* Upload Zone (classique + assurance) */}
        {mode !== "rapide" && (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-300 hover:border-sky-400 transition p-6 md:p-8 text-center" data-testid="upload-zone">
            {scanning ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 animate-spin text-sky-500" />
                <p className="text-sm font-semibold text-slate-600">Analyse clinique en cours...</p>
                <p className="text-xs text-slate-400">L'IA verifie interactions, posologies et contre-indications</p>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {["OCR", "Interactions", "Posologie", "Alertes"].map((step, i) => (
                    <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-sky-50 text-sky-600 font-medium animate-pulse" style={{ animationDelay: `${i * 0.3}s` }}>{step}</span>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <ScanLine className={`w-12 h-12 mx-auto mb-3 ${mode === "classique" ? "text-sky-400" : "text-violet-400"}`} />
                <p className="text-sm font-semibold text-slate-700 mb-1">
                  {mode === "classique" ? "Scanner une ordonnance" : "Scanner un bon d'assurance"}
                </p>
                <p className="text-xs text-slate-400 mb-4">Photo, scan ou PDF — meme manuscrit</p>
                <label className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white cursor-pointer transition active:scale-95 ${mode === "classique" ? "bg-sky-600 hover:bg-sky-700" : "bg-violet-600 hover:bg-violet-700"}`}
                  data-testid="upload-button">
                  <Upload className="w-4 h-4" /> Charger l'ordonnance
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="prescription-file-input"
                    onChange={e => { if (e.target.files[0]) handleScan(e.target.files[0]); }} />
                </label>
              </>
            )}
          </div>
        )}

        {/* Quick Check Input (rapide mode) */}
        {mode === "rapide" && (
          <QuickCheckInput
            selectedMeds={selectedMeds}
            setSelectedMeds={setSelectedMeds}
            onCheck={handleQuickCheck}
            checking={checking}
          />
        )}

        {/* ===== RESULTS ===== */}
        {result && (
          <div className="space-y-4" data-testid="scan-results">
            {/* Score Banner */}
            <div className={`rounded-xl p-5 border ${statusStyle?.light} ${statusStyle?.border}`} data-testid="score-banner">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {StatusIcon && <StatusIcon className={`w-8 h-8 ${statusStyle.text}`} />}
                  <div>
                    <h3 className={`text-lg font-bold ${statusStyle.text}`}>{scoring.statut_label}</h3>
                    <p className="text-xs text-slate-500">Score : {scoring.score}/{scoring.max_score} — {result.total_alertes} alerte(s)</p>
                  </div>
                </div>
                <div className="text-right">
                  {result.montant_total_estime > 0 && (
                    <>
                      <p className="text-xs text-slate-500">Montant estime</p>
                      <p className="text-lg font-bold text-slate-800">{fmt(result.montant_total_estime)} F</p>
                      {result.taux_remboursement && <p className="text-xs text-violet-600 font-semibold">Remb. : {result.taux_remboursement}%</p>}
                    </>
                  )}
                  <div className="mt-2 flex items-center gap-1.5 justify-end">
                    <button
                      type="button"
                      onClick={handlePrintConseil}
                      data-testid="print-conseil-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 active:scale-[0.98] transition text-white text-xs font-semibold shadow-sm"
                      title="Imprimer le livret conseil patient avec calendrier d'observance (7j × 3 prises par defaut)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                      Livret patient
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowObservance(v => !v)}
                      data-testid="toggle-observance-btn"
                      className={`px-2 py-1.5 rounded-lg text-xs font-semibold transition border ${showObservance ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
                      title="Personnaliser le calendrier d'observance par medicament"
                    >
                      ⚙️
                    </button>
                  </div>
                </div>
              </div>

              {showObservance && (result.medicaments_resolus || []).filter(m => m.connu_en_base).length > 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="observance-panel">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wide">Calendrier d'observance — personnalisation</h4>
                    <span className="text-[10px] text-amber-700">Par défaut : 7 jours · 3 prises/jour</span>
                  </div>
                  <div className="space-y-1.5">
                    {(result.medicaments_resolus || []).filter(m => m.connu_en_base).map((m, idx) => {
                      const dci = m.dci || m.nom_original;
                      const cfg = observanceConfig[dci] || {};
                      return (
                        <div key={idx} className="flex items-center gap-2 text-xs bg-white rounded px-2 py-1.5 border border-amber-100">
                          <span className="flex-1 font-semibold text-slate-700 truncate">{m.nom_original}</span>
                          <label className="text-[10px] text-slate-500">Durée</label>
                          <input
                            type="number" min="1" max="14"
                            placeholder="7"
                            value={cfg.duree || ""}
                            onChange={e => setObservanceConfig(prev => ({...prev, [dci]: {...prev[dci], duree: e.target.value}}))}
                            className="w-12 px-1.5 py-0.5 border border-slate-200 rounded text-center text-xs"
                            data-testid={`obs-duree-${idx}`}
                          />
                          <span className="text-[10px] text-slate-400">j</span>
                          <label className="text-[10px] text-slate-500 ml-1">Prises</label>
                          <select
                            value={cfg.freq || ""}
                            onChange={e => setObservanceConfig(prev => ({...prev, [dci]: {...prev[dci], freq: e.target.value}}))}
                            className="px-1.5 py-0.5 border border-slate-200 rounded text-xs"
                            data-testid={`obs-freq-${idx}`}
                          >
                            <option value="">3 (défaut)</option>
                            <option value="1">1×/j</option>
                            <option value="2">2×/j</option>
                            <option value="3">3×/j</option>
                            <option value="4">4×/j</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {hasPatientProfile && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {result.patient_profil.age_mois && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-teal-100 text-teal-700 font-semibold">
                      {result.patient_profil.age_mois < 24 ? `${result.patient_profil.age_mois} mois` : `${Math.floor(result.patient_profil.age_mois / 12)} ans`}
                    </span>
                  )}
                  {result.patient_profil.poids_kg && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-teal-100 text-teal-700 font-semibold">{result.patient_profil.poids_kg} kg</span>
                  )}
                  {(result.patient_profil.statuts || []).map(s => (
                    <span key={s} className="text-[10px] px-2 py-1 rounded-full bg-pink-100 text-pink-700 font-semibold">
                      {s === "grossesse" ? "Grossesse" : s === "allaitement" ? "Allaitement" : s === "insuffisance_renale" ? "Insuff. renale" : s === "insuffisance_hepatique" ? "Insuff. hepatique" : s}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Ordonnance Info (only for scan modes) */}
            {result.ordonnance && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                    <Stethoscope className="w-3.5 h-3.5" /> Prescripteur
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-slate-700"><span className="text-slate-400 text-xs">Medecin :</span> {result.ordonnance?.medecin?.nom || "Non identifie"}</p>
                    <p className="text-slate-700"><span className="text-slate-400 text-xs">Specialite :</span> {result.ordonnance?.medecin?.specialite || "Non precisee"}</p>
                    <div className="flex gap-3 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${result.ordonnance?.medecin?.cachet_present ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        Cachet {result.ordonnance?.medecin?.cachet_present ? "OK" : "absent"}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${result.ordonnance?.medecin?.signature_presente ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        Signature {result.ordonnance?.medecin?.signature_presente ? "OK" : "absente"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> Informations
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-slate-700"><span className="text-slate-400 text-xs">Patient :</span> {result.ordonnance?.patient?.nom || "Non identifie"}</p>
                    <p className="text-slate-700"><span className="text-slate-400 text-xs">Date :</span> {result.ordonnance?.date_prescription || "Non lisible"}</p>
                    {mode === "assurance" && <p className="text-slate-700"><span className="text-slate-400 text-xs">N. Assure :</span> {result.ordonnance?.patient?.numero_assure || "Non visible"}</p>}
                    {result.ordonnance?.ratures_corrections && <p className="text-red-600 text-xs font-bold mt-1">Ratures / corrections detectees</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Medications */}
            <MedicationTable medicaments={result.medicaments} hasPatientProfile={hasPatientProfile} />

            {/* Clinical Alerts */}
            <AlertSection cat="clinique" alerts={result.alertes?.clinique} />

            {/* Patient Counseling */}
            <ConseilsPatientSection conseils={result.conseils_patient} />

            {/* Administrative + Insurance Alerts */}
            <AlertSection cat="administratif" alerts={result.alertes?.administratif} />
            <AlertSection cat="assurance" alerts={result.alertes?.assurance} />

            {/* Sources */}
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-center">
              <p className="text-[10px] text-slate-400">
                Sources : VIDAL | Dorosz | ANSM | Lexicomp | Micromedex — Base locale Cote d'Ivoire ({result.medicaments?.filter(m => m.connu_en_base).length || 0}/{result.medicaments?.length || 0} references)
              </p>
            </div>

            {result.total_alertes === 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-emerald-700">Aucune alerte detectee</p>
                <p className="text-xs text-emerald-500 mt-1">Aucune interaction ou contre-indication identifiee</p>
              </div>
            )}
          </div>
        )}

        {/* History */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <button onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between p-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition" data-testid="toggle-history">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-400" /> Historique ({history.length})
            </div>
            {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {showHistory && history.length > 0 && (
            <div className="px-4 pb-4 space-y-2">
              {history.map(h => {
                const st = STATUS_STYLES[h.scoring?.statut] || {};
                const modeLabel = h.mode === "assurance" ? h.assurance : h.mode === "verification_rapide" ? "Rapide" : "Classique";
                const modeBg = h.mode === "assurance" ? "bg-violet-100 text-violet-700" : h.mode === "verification_rapide" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700";
                return (
                  <div key={h.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-xs">
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${st.bg}`} />
                      <span className="text-slate-700 font-medium">{h.scoring?.statut_label || "--"}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${modeBg}`}>{modeLabel}</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-500">
                      <span>{fmt(h.montant_total_estime || 0)} F</span>
                      <span>{new Date(h.created_at).toLocaleDateString("fr-FR")}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PharmatechLayout>
  );
}
